<template>
  <div>
    <Header>
      保有資格
    </Header>

    <div class="pt-2 pb-4 mx-3">
      <StepBar :maxValue="4" :currentValue="2" class="mb-3" />
      <p class="mb-3 text-size-14 text-mild">
        より多くの資格証をご登録いただくと、応募可能な仕事が増えます
      </p>

      <!-- 元々追加されていた資格 -->
      <QualificationItem
        v-for="(qualification, index) in qualifications"
        :key="index"
        :status="qualification.status"
        class="mt-2"
        >{{
          getQualificationName(qualification.qualification)
        }}</QualificationItem
      >

      <!-- 新規登録のタイミングで追加する資格 -->
      <QualificationItem
        v-for="(qualification, index) in inputtedQualifications"
        :key="index"
        status="unverified"
        class="mt-2"
      >
        {{
          getQualificationName(qualification.qualification)
        }}</QualificationItem
      >

      <AddQualificationItem
        :to="{ name: 'RegisterAddQualification' }"
        class="mt-2"
      />

      <BottomFormActionButtons
        submitButtonText="次へ"
        skipButtonText="スキップする"
        @submit="onClickSubmit"
        @skip="onClickSkip"
      />
    </div>
    <BlockBack />
  </div>
</template>

<script lang="ts">
import { AbTest3113 } from "@/app/services/AbTestService";
import { accountService } from "@/app/services/AccountService";
import AddQualificationItem from "@/components/molecules/other/AddQualificationItem.vue";
import QualificationItem from "@/components/molecules/other/QualificationItem.vue";
import StepBar from "@/components/molecules/other/StepBar.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import Header from "@/components/organisms/common/Header.vue";
import BottomFormActionButtons from "@/components/organisms/register/BottomFormActionButtons.vue";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { authStore } from "@/store/auth";
import { masterStore } from "@/store/master";
import { registerStore } from "@/store/register";
import { UserQualification } from "@/types/qualification/UserQualification";
import { routerPush } from "@/utils/routerPush";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Vue } from "vue-facing-decorator";

@Component({
  components: {
    BlockBack,
    Header,
    AddQualificationItem,
    QualificationItem,
    BottomFormActionButtons,
    StepBar
  }
})
export default class RegisterSetQualification extends Vue {
  qualifications: UserQualification[] = [];

  get inputtedQualifications() {
    return registerStore.qualifications;
  }

  get hasQualification() {
    return (
      this.inputtedQualifications.length > 0 || this.qualifications.length > 0
    );
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchQualifications(),
        // この段階（資格登録のフロー）でアカウント認証に必要な情報は揃っているはずなので、
        // ここで initialProfileRegister（初回のアカウント認証リクエスト）を実行する。
        this.initialProfileRegister()
      ]);
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchQualifications() {
    const { results } = await accountService.fetchQualifications();
    this.qualifications = results;
  }

  getQualificationName(qualificationId: number) {
    return masterStore.qualificationMap.get(qualificationId)!.name;
  }

  async initialProfileRegister() {
    // AddQualification から戻って来る場合を考慮して、初回認証申請を送信済みかどうかを確認する
    if (!authStore.hasRegisteredProfile) {
      await accountService.initialProfileRegister();
      const userData = await accountService.fetchUserData();
      authStore.changeUserInfo(userData);
      AbTest3113.registerEvent(
        AbTest3113.eventTypes.completeAccountApplication
      );
    }
  }

  async onClickSubmit() {
    if (!this.hasQualification) {
      this.$bvModal.msgBoxOk("保有資格を1つ以上追加してください", {
        centered: true
      });
      return;
    }
    try {
      this.$loading.show();
      await Promise.all(
        this.inputtedQualifications.map(qualification => {
          return accountService.createQualifications(
            qualification.image,
            qualification.qualification
          );
        })
      );
      AbTest3113.registerEvent(
        AbTest3113.eventTypes.completeQualificationRegistration
      );
      sendAdjustEvent("FINISH_APPLY_QUALIFICATION");
      sendFirebaseCustomEvent("finish_apply_qualification", {
        session_id: authStore.sessionId || "unknown"
      });

      await registerStore.clearQualifications();
      await routerPush(this, { name: "RegisterSetBank" });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async onClickSkip() {
    sendAdjustEvent("SKIP_APPLY_QUALIFICATION");
    sendFirebaseCustomEvent("skip_apply_qualification", {
      session_id: authStore.sessionId || "unknown"
    });
    await routerPush(this, { name: "RegisterFinishSignup" });
  }
}
</script>
